/**
 * Shared <SkillContentEditor> — Identity + Description + Instruction body +
 * Skill Helper for Catalog Skills (PR-W22B, 2026-05-23).
 *
 * Mounted by:
 *   - routes/sysadmin/catalog.skills.index.tsx  (CreateSkillSheet, mode="create")
 *   - routes/sysadmin/catalog.skills.$id.tsx    (OverviewTab,      mode="edit")
 *
 * Closes issue #587 — "Sysadmin Catalog: Skills route is not a thin shared
 * SkillEditor wrapper". The Studio SkillEditor (scope='tenant' | 'sysadmin')
 * exists already; this component extracts the truly-shared editor body of
 * the *catalog* surface so the index CreateSkillSheet and the detail
 * OverviewTab stop duplicating ~250 LOC of form chrome each.
 *
 * Lifecycle action bar, Composition / Versions / Assignments / Audit tabs,
 * dangling-reference banners and the table list remain catalog-specific —
 * they are not editor surface and would over-couple if shared. This matches
 * the founder-locked pattern from the Library divergence catch (2026-05-11):
 * ONE shared component with a scope/mode prop for the truly-overlapping
 * editor body, NOT two parallel files.
 */

import { useState } from 'react';
import {
  Pencil, Save, Loader2, X, Sparkles as SparklesIcon,
} from 'lucide-react';
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import {
  Collapsible, CollapsibleContent, CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { HelperPanel } from '@/components/studio/helper-panel';
import { MarkdownRenderer } from '@/components/chat/MarkdownRenderer';
import type { PendingMutation } from '@/lib/use-studio-helper';

// ─── Public draft shape ────────────────────────────────────────────────────

export interface SkillContentDraft {
  slug: string;
  displayName: string;
  description: string;
  instructions: string;
  category: string;
}

// ─── Mode discriminator ─────────────────────────────────────────────────────
//
// `create`: slug is editable; whole form is always "editing"; no edit
//           chrome; helper canvas does not include tool/function deps.
// `edit`  : slug is read-only; edit chrome toggles editing per request;
//           helper canvas can carry tool/function deps; read-only state
//           (retired) hides helper + chrome.

export type SkillContentEditorMode = 'create' | 'edit';

export interface SkillContentEditorProps {
  mode: SkillContentEditorMode;
  draft: SkillContentDraft;
  onDraftChange: (next: SkillContentDraft) => void;

  /** edit-mode only: snapshot of the row used to reset the draft on cancel. */
  baseline?: SkillContentDraft;

  /** edit-mode only: triggered by the Save button in the edit chrome. */
  onSave?: () => void | Promise<void>;

  /** edit-mode only: whether the save mutation is in-flight. */
  saving?: boolean;

  /** edit-mode only: when true, hides edit chrome + helper (e.g. retired). */
  readOnly?: boolean;

  /**
   * Tool/function dependencies — only surfaced to the Skill Helper as
   * canvas state. Optional; the create flow does not yet have these refs.
   */
  toolDependencies?: string[];
  functionDependencies?: string[];

  /**
   * create-mode only: when true the slug input is rendered. The detail
   * page never edits the slug (immutable post-create) and renders it
   * as plain text.
   */
  immutableSlug?: boolean;

  /**
   * Optional helper-panel intro override. Defaults to a generic copy.
   */
  helperIntro?: string;

  /**
   * Optional className passed to the outer wrapper.
   */
  className?: string;
}

// ─── Component ──────────────────────────────────────────────────────────────

export function SkillContentEditor({
  mode,
  draft,
  onDraftChange,
  baseline,
  onSave,
  saving = false,
  readOnly = false,
  toolDependencies,
  functionDependencies,
  immutableSlug,
  helperIntro,
  className,
}: SkillContentEditorProps) {
  // edit-mode toggle. In create-mode the form is always "editing" so we
  // skip the chrome and treat editing as always-true.
  const [editing, setEditing] = useState(false);
  const isEditing = mode === 'create' || editing;
  const isCreate = mode === 'create';

  // Helper-panel intro copy — caller can override.
  const defaultIntro = isCreate
    ? "Tell me what platform skill you need. I'll ask a few clarifying questions, then draft the description and instruction body — you Accept or Reject each suggestion. Tool / function references are added on the Composition tab after create."
    : "Tell me what platform skill you need. I'll ask a few clarifying questions, then draft the description and instruction body — you Accept or Reject each suggestion. Tool / function references are added on the Composition tab.";

  const renderEditChrome = () => {
    if (isCreate) return null;
    if (readOnly) return null;
    if (editing) {
      return (
        <div className="flex gap-1.5">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setEditing(false);
              if (baseline) onDraftChange(baseline);
            }}
            title="Discard changes"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
          <Button
            size="sm"
            onClick={async () => {
              if (onSave) {
                await onSave();
                // Caller is responsible for clearing in-flight state +
                // optionally toggling editing back off after success;
                // we toggle off here so a successful save closes the
                // local editing toggle without forcing the caller to
                // thread state back.
                setEditing(false);
              }
            }}
            disabled={saving}
            title="Save changes (creates a new version)"
          >
            {saving ? (
              <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
            ) : (
              <Save className="h-3.5 w-3.5 mr-1.5" />
            )}
            Save
          </Button>
        </div>
      );
    }
    return (
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setEditing(true)}
        title="Edit"
      >
        <Pencil className="h-3.5 w-3.5 mr-1.5" />
        Edit
      </Button>
    );
  };

  const set = (patch: Partial<SkillContentDraft>) =>
    onDraftChange({ ...draft, ...patch });

  return (
    <div className={`space-y-4 ${className ?? ''}`}>
      {/* Identity — slug + category + display name */}
      <Card>
        <CardHeader className="flex flex-row items-start justify-between pb-3">
          <div>
            <CardTitle className={isCreate ? 'text-sm' : 'text-base'}>
              Identity
            </CardTitle>
            <CardDescription className={isCreate ? 'text-xs' : undefined}>
              {isCreate
                ? 'Slug is the URL-safe identifier; immutable after creation.'
                : 'Display metadata. Slug is immutable after creation.'}
            </CardDescription>
          </div>
          {renderEditChrome()}
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className={isCreate ? 'space-y-1.5' : undefined}>
              <Label
                htmlFor="sce-slug"
                className={isCreate ? 'text-xs' : 'text-xs text-muted-foreground'}
              >
                Slug
              </Label>
              {isCreate && !immutableSlug ? (
                <>
                  <Input
                    id="sce-slug"
                    value={draft.slug}
                    onChange={(e) =>
                      set({
                        slug: e.target.value
                          .toLowerCase()
                          .replace(/[^a-z0-9_.-]/g, ''),
                      })
                    }
                    placeholder="equipment-lookup"
                    className="font-mono text-xs h-8"
                  />
                  <p className="text-[10px] text-muted-foreground">
                    lowercase. a-z, 0-9, _ - .
                  </p>
                </>
              ) : (
                <div className="font-mono text-xs mt-1">{draft.slug || '—'}</div>
              )}
            </div>
            <div className={isCreate ? 'space-y-1.5' : undefined}>
              <Label
                htmlFor="sce-category"
                className={isCreate ? 'text-xs' : 'text-xs text-muted-foreground'}
              >
                Category
              </Label>
              {isEditing ? (
                <Input
                  id="sce-category"
                  value={draft.category}
                  onChange={(e) => set({ category: e.target.value })}
                  placeholder={isCreate ? 'general' : undefined}
                  className={
                    isCreate
                      ? 'text-xs h-8'
                      : 'text-xs h-8 mt-1'
                  }
                />
              ) : (
                <div className="text-sm mt-1">{draft.category}</div>
              )}
            </div>
          </div>
          <div className={isCreate ? 'space-y-1.5' : undefined}>
            <Label
              htmlFor="sce-name"
              className={isCreate ? 'text-xs' : 'text-xs text-muted-foreground'}
            >
              Display name
            </Label>
            {isEditing ? (
              <Input
                id="sce-name"
                value={draft.displayName}
                onChange={(e) => set({ displayName: e.target.value })}
                placeholder={isCreate ? 'Equipment Lookup' : undefined}
                className={
                  isCreate
                    ? 'text-sm h-8'
                    : 'text-sm h-8 mt-1'
                }
              />
            ) : (
              <div className="text-sm mt-1">{draft.displayName}</div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Description — purpose, when to use, summary */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className={isCreate ? 'text-sm' : 'text-base'}>
            Description
          </CardTitle>
          <CardDescription className={isCreate ? 'text-xs' : undefined}>
            {isCreate
              ? 'What this skill does and when an agent should compose with it.'
              : 'What this skill does and when an agent should compose with it. Surfaced to tenants on the skill detail card.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isEditing ? (
            <Textarea
              id="sce-desc"
              value={draft.description}
              onChange={(e) => set({ description: e.target.value })}
              placeholder="What this skill does and when to use it."
              rows={3}
              className="text-sm"
            />
          ) : (
            <div className="text-sm">{draft.description || '—'}</div>
          )}
        </CardContent>
      </Card>

      {/* Instruction body — Markdown body with live preview */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className={isCreate ? 'text-sm' : 'text-base'}>
            Instruction body
          </CardTitle>
          <CardDescription className={isCreate ? 'text-xs' : undefined}>
            {isCreate
              ? 'The Markdown body composed into agent prompts. Reference tool / function slugs the skill calls — wire the actual refs on the Composition tab after create.'
              : "The skill's prompt body. Markdown is rendered for tenants — edit on the left, preview on the right."}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isEditing ? (
            isCreate ? (
              <Textarea
                id="sce-instructions"
                value={draft.instructions}
                onChange={(e) => set({ instructions: e.target.value })}
                placeholder={
                  'When the user asks about equipment, use `asset_lookup` first to confirm the unit, then call `asset_detail` for full specs. Always cite the asset tag in the response.'
                }
                rows={10}
                className="text-sm font-mono"
              />
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">
                    Edit (Markdown)
                  </Label>
                  <Textarea
                    value={draft.instructions}
                    onChange={(e) => set({ instructions: e.target.value })}
                    rows={20}
                    className="text-sm font-mono"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Preview</Label>
                  <div className="border rounded-md p-3 max-h-[480px] overflow-y-auto bg-background">
                    <MarkdownRenderer
                      content={draft.instructions || '_(empty)_'}
                    />
                  </div>
                </div>
              </div>
            )
          ) : (
            <div className="border rounded-md p-3 max-h-[480px] overflow-y-auto bg-background">
              <MarkdownRenderer content={draft.instructions || '_(empty)_'} />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Skill Helper — AI assist (Phase 1 feature expansion).
          Hidden in read-only mode (retired) and when not editing in the
          detail flow so the helper doesn't suggest patches the admin
          can't accept. In create-mode the helper is always visible since
          the form is implicitly always editing. */}
      {!readOnly && isEditing && (
        <Collapsible className="rounded border bg-violet-500/5">
          <CollapsibleTrigger className="flex w-full items-center gap-2 px-3 py-2 text-xs font-medium text-violet-700 dark:text-violet-300 hover:bg-violet-500/10 rounded">
            <SparklesIcon className="h-3.5 w-3.5" />
            Draft with Skill Helper (AI)
          </CollapsibleTrigger>
          <CollapsibleContent className="border-t">
            <HelperPanel
              kind="skill_helper"
              scope="sysadmin"
              intro={helperIntro ?? defaultIntro}
              className="border-0 rounded-none min-h-[320px]"
              canvasState={
                {
                  slug: draft.slug,
                  displayName: draft.displayName,
                  description: draft.description,
                  instructions: draft.instructions,
                  category: draft.category,
                  ...(toolDependencies ? { toolDependencies } : {}),
                  ...(functionDependencies ? { functionDependencies } : {}),
                } as Record<string, unknown>
              }
              onApplyMutation={(m: PendingMutation) => {
                const a = m.args as Record<string, unknown>;
                switch (m.toolName) {
                  case 'set_metadata':
                    // Helper may suggest a slug too — only accept if the
                    // user hasn't typed one yet AND the slug input is
                    // mutable (create-mode without immutableSlug). The
                    // detail flow never accepts slug patches because the
                    // slug is immutable post-create.
                    if (
                      a.slug != null &&
                      isCreate &&
                      !immutableSlug &&
                      !draft.slug
                    ) {
                      onDraftChange({
                        ...draft,
                        slug: String(a.slug)
                          .toLowerCase()
                          .replace(/[^a-z0-9_.-]/g, ''),
                      });
                      return;
                    }
                    {
                      const patch: Partial<SkillContentDraft> = {};
                      if (a.displayName != null)
                        patch.displayName = String(a.displayName);
                      if (a.description != null)
                        patch.description = String(a.description);
                      if (a.category != null)
                        patch.category = String(a.category);
                      if (Object.keys(patch).length > 0) set(patch);
                    }
                    break;
                  case 'set_instructions':
                    if (a.instructionsMd != null)
                      set({ instructions: String(a.instructionsMd) });
                    break;
                }
              }}
            />
          </CollapsibleContent>
        </Collapsible>
      )}
    </div>
  );
}
